import { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Upload,
  Copy,
  Trash2,
  MoreHorizontal,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


interface FileDoc {
  _id: string;
  originalname: string;
  mimeType: string;
  size: number;
  url?: string;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

export function Files() {
  const [files, setFiles] = useState<FileDoc[]>([]);
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [editingFile, setEditingFile] = useState<FileDoc | null>(null);
  const [newFileName, setNewFileName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalFileCount = files.length;
  const totalFileSize = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files],
  );

  const filteredFiles = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();
    if (!query) return files;
    return files.filter((file) =>
      file.originalname.toLowerCase().includes(query),
    );
  }, [files, nameSearch]);

  const totalCount = filteredFiles.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredFiles.slice(start, start + pageSize);
  }, [filteredFiles, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [nameSearch, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
  };

  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      const [filesRes, domainsRes] = await Promise.all([
        fetch("/api/files"),
        fetch("/api/custom-domains"),
      ]);
      const filesData = await filesRes.json();
      const domainsData = await domainsRes.json();
      setFiles(filesData.files || []);
      const domains = domainsData.customDomains || [];
      setCustomDomainHostname(domains[0]?.hostname ?? null);
    } catch (error) {
      console.error("Failed to fetch files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
    setBreadcrumbs([
      { label: "Website", path: "/pages" },
      { label: "Files", path: "/files" },
    ]);
    return () => clearBreadcrumbs();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/files", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to upload file");
      }

      await fetchFiles();
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  const copyUrl = (fileId: string) => {
    const baseUrl = customDomainHostname
      ? `https://${customDomainHostname}`
      : window.location.origin;
    const url = `${baseUrl}/files/${fileId}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm("Are you sure you want to delete this file?")) {
      return;
    }

    setDeletingId(fileId);
    try {
      const response = await fetch(`/api/files/${fileId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete file");
      }

      toast.success("File deleted successfully");
      await fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete file");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveName = async () => {
    if (!editingFile || !newFileName.trim()) return;

    setIsSavingName(true);
    try {
      const response = await fetch(`/api/files/${editingFile._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ originalname: newFileName.trim() }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update file name");
      }

      toast.success("File name updated successfully");
      setEditingFile(null);
      await fetchFiles();
    } catch (error) {
      console.error("Error updating file name:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update file name");
    } finally {
      setIsSavingName(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Files</h1>
        <div className="flex gap-2">
          <input
            ref={uploadInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={isUploading}
          />
          <Button
            variant="default"
            size="sm"
            onClick={() => uploadInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Card>
          <CardContent className="pt-6 pb-6">
            <div className="text-center">
              <p className="text-3xl font-bold tabular-nums">
                {totalFileCount.toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">Files</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 pb-6">
            <div className="text-center">
              <p className="text-3xl font-bold tabular-nums">
                {formatSize(totalFileSize)}
              </p>
              <p className="text-muted-foreground mt-2">Total Size</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by name"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Preview</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Size</TableHead>
            <TableHead className="w-[72px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedFiles.length > 0 ? (
            paginatedFiles.map((file) => (
              <TableRow key={file._id}>
                <TableCell className="align-middle">
                  {isImageMimeType(file.mimeType) && file.url ? (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block size-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted"
                    >
                      <img
                        src={file.url}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>{file.originalname}</TableCell>
                <TableCell>{file.mimeType}</TableCell>
                <TableCell>{formatSize(file.size)}</TableCell>
                <TableCell className="p-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="File actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {file.url ? (
                        <>
                          <DropdownMenuItem onClick={() => copyUrl(file._id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem
                        onClick={() => {
                          setEditingFile(file);
                          setNewFileName(file.originalname);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => handleDelete(file._id)}
                        disabled={deletingId === file._id}
                      >
                        {deletingId === file._id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                {files.length === 0
                  ? "No files found. Upload a file to get started."
                  : "No files match your search."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {(totalPages > 1 || totalCount > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger size="sm" className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {totalCount} {totalCount === 1 ? "file" : "files"}
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editingFile} onOpenChange={(open) => { if (!open) setEditingFile(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit File Name</DialogTitle>
            <DialogDescription>
              Enter a new name for this file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fileName">File Name</Label>
              <Input
                id="fileName"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFileName.trim() && !isSavingName) {
                    handleSaveName();
                  }
                }}
                autoFocus
                disabled={isSavingName}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingFile(null)}
              disabled={isSavingName}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveName}
              disabled={!newFileName.trim() || isSavingName}
            >
              {isSavingName ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
